import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { AxiosResponse } from 'axios'
import { useAtom } from 'jotai'
import { useParams, useSearchParams } from 'react-router-dom'
import { getCaseDetails } from '../../../../api/salary'
import { CaseDetail } from '../../../../models/CaseDetail'
import { CardColumnContainer } from '../../../ui/Card/CardColumnContainer'
import { Typography } from '../../../ui/Typography/Typography'
import { BackLink } from '../../../ui/BackLink'
import { L } from '../../../providers/Lokalise'
import { colors } from '../../../../theme/palette'
import PersonalDetails from './personalDetails'
import { CaseFilesRow } from '../CaseFiles/CaseFilesRow'
import { CaseNotesRow } from '../CaseNotes/CaseNotesRow'
import {
    IkonAfvissag,
    IkonSammenlaeg,
    LockFill,
    ThreeDots,
} from '../../../../assets/icons'
import Reminders from '../Reminders/Reminders'
import { MergedCasesRow } from '../MergedCases/MergedCasesRow'
import { Spinner } from '../../../ui/Loaders/Spinner'
import { Dropdown } from '../../../ui/Dropdown'
import { Button } from '../../../ui/Button/Button'
import { RejectCase } from '../RejectCase'
import { AttachToAnotherCase } from '../AttachToAnotherCase'
import { Case } from '../../../../models/Case'
import { CaseReportingRow } from '../CaseReporting/CaseReportingRow'
import { CaseDetailsPrompt } from './CaseDetailsPrompt'
import { CloseCaseRow } from './CloseCaseRow'
import { TakeCase } from '../TakeCase'
import { profileAtom } from '../../../../api/profile/profile'
import { ContactDialog, contactDialogStateAtom } from '../Contact/ContactDialog'
import { formatDate } from '../../../../utils/format'
import {
    fetchCasesAtom,
    openCasesAtom,
} from '../../../../store/filter/filterCasesAtom'
import { CaseHelpColumn } from './CaseHelpColumn'
import breakpoints from '../../../../theme/breakpoints'
import { useWindowResize } from '../../../../hooks/useWindowSize'
import { HeaderLinks } from '../../../modules/Headerlinks'

export const useStyles = createUseStyles({
    profileHeader: {
        padding: '0',
        width: '100%',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            padding: '3rem 0 0',
        },
    },
    headerDetails: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 0 0',
        gap: '1rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            flexDirection: 'row',
        },
    },
    nameContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            justifyContent: 'flex-start',
        },
    },
    header: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '1.5rem',
    },
    section: {
        marginBottom: '3rem',

        '& > hr': {
            opacity: '0.2',
            margin: '1.25rem 0',
        },
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: '100%',
        position: 'relative',
    },
    accordionButton: {
        background: 'none',
        border: 'none',
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.5rem',
        position: 'absolute',
        right: '-0.5rem',
        cursor: 'pointer',

        '& svg': {
            color: colors.black,
            width: '1rem',
            height: '1rem',
            transform: 'rotate(90deg)',
            transition: 'transform 0.3s ease',
        },
        '&[aria-expanded="true"] svg': {
            transform: 'rotate(270deg)',
        },
    },
    accordionContent: {
        display: 'grid',
        gap: '0.5rem',
        marginTop: '0rem',
        gridTemplateRows: '0fr',

        transition: 'all 0.3s ease',

        '&.open': {
            gridTemplateRows: '1fr',
            marginTop: '1rem',
        },
        '& a': {
            display: 'block',
            color: colors.black,
            textDecoration: 'underline',
            marginBlock: '0.5rem',
        },
    },
    headerActions: {
        display: 'flex',
        flexGrow: 1,
        marginLeft: 'auto',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        flexShrink: 0,
    },
    headerArea: {
        display: 'flex',
        padding: '.5rem 0',
        minHeight: '5rem',
    },
    headerAreaTop: {
        display: 'flex',
        padding: '1rem 0 .5rem',
        borderBottom: `1px solid ${colors.borderColor}`,
        marginBottom: '1rem',
    },
    headerMobile: {
        position: 'relative',
        marginTop: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '.65rem',
        backgroundColor: colors.white,
        padding: '1rem',
        borderRadius: '.5rem',
    },
    headerMobileDetails: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '.5rem',
    },
    takeCaseContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        '& button': {
            width: '100%',
        },
    },
    container: {
        backgroundColor: 'white',
        borderRadius: '6px',
        position: 'absolute',
        top: '20%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90vw',
        maxWidth: '500px',
        maxHeight: '85vh',
        padding: '2.75rem',
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        zIndex: 0,
        '&.withIcon': {
            paddingTop: '80px',
        },
        '&.large': {
            maxWidth: '800px',
        },
    },
    circleIcon: {
        position: 'absolute',
        top: '-25px',
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        backgroundColor: colors.blue300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '1rem',
        border: `3px solid ${colors.white}`,
    },
})
const CaseDetails: React.FC = (): JSX.Element => {
    const windowSize = useWindowResize()
    const [authProfile] = useAtom(profileAtom)
    const classes = useStyles()
    const params = useParams()
    const [searchParams] = useSearchParams()
    const [caseDetails, setCaseDetails] = useState<CaseDetail | null>(null)
    const [open, setOpen] = useState(false)
    const [caseDetailsError, setCaseDetailsError] = useState<any>(null)
    const toggleOpen = (): void => {
        setOpen(!open)
    }
    const [isLoading, setIsLoading] = useState(false)
    const [attachCaseOpen, setAttachCaseOpen] = useState(false)
    const [rejectCaseOpen, setRejectCaseOpen] = useState(false)
    const [openCases] = useAtom(openCasesAtom)
    const [, fetchCases] = useAtom(fetchCasesAtom)
    const [, setContactDialogState] = useAtom(contactDialogStateAtom)

    const init = async (id: string): Promise<AxiosResponse> => {
        setIsLoading(true)
        const response = await getCaseDetails(id)
        if (response.error) {
            setCaseDetailsError(response.error)
            setIsLoading(false)
            return Promise.resolve({} as AxiosResponse)
        }
        const details = CaseDetail.build(response)

        setCaseDetails(details)
        fetchCases()

        setIsLoading(false)
        return Promise.resolve({} as AxiosResponse)
    }

    useEffect(() => {
        // Supports both /sag/:id and /sag?sagsId=<id> (link format used in emails)
        const id = params.id ?? searchParams.get('sagsId')

        if (id) {
            init(id)
        }

        // Cleanup function to reset dialog state when navigating away
        return () => {
            setContactDialogState({
                caseId: '',
                template: '',
                content: '',
                selectedFileList: [],
                to: '',
                subject: '',
                cc: '',
                bcc: '',
            })
        }
    }, [params, searchParams, setContactDialogState])

    return (
        <>
            {caseDetailsError ? (
                <div className={`${classes.container} withIcon`}>
                    <div className={classes.circleIcon}>
                        <LockFill width="25px" />
                    </div>

                    <Typography tag="p" size="XS">
                        {caseDetailsError.errors.Parameter[0]}
                    </Typography>
                </div>
            ) : (
                <div>
                    {!!caseDetails?.showAcceptDialog &&
                        !authProfile.isImitator && (
                            <CaseDetailsPrompt
                                caseDetails={caseDetails}
                                caseId={caseDetails?.caseId}
                                caseRegistrationDate={
                                    caseDetails?.caseRegistrationTime
                                }
                                onSuccess={() => {
                                    init(caseDetails.caseId)
                                }}
                            />
                        )}
                    {windowSize.width > breakpoints.Desktop && (
                        <div className={classes.header}>
                            <div
                                className={`${classes.headerAreaTop} desktop-only`}
                            >
                                <div className={classes.headerActions}>
                                    <HeaderLinks
                                        showGotoMembrsearchModal={false}
                                    />
                                </div>
                            </div>
                            <div>
                                <BackLink
                                    to={`/mine-lonindplaceringer${
                                        localStorage.getItem(
                                            'salary-last-tab'
                                        ) === 'closed'
                                            ? '?tab=lukket'
                                            : ''
                                    }`}
                                    label={L(
                                        'HEADER_FANEBLAD_LONINDPLACERINGER-TITEL'
                                    )}
                                />
                                <div className={classes.profileHeader}>
                                    <div className={classes.nameContainer}>
                                        <Typography
                                            mobileSize="XL"
                                            tag="h1"
                                            size="XXXXL"
                                            spacing=".2rem"
                                        >
                                            {caseDetails?.newlyHired.name}
                                        </Typography>
                                    </div>
                                    <div className={classes.headerDetails}>
                                        <Typography tag="p" size="XS">
                                            {L('LØN_DETALJE_CASE_ID_LABEL')}
                                            {': '}
                                            {caseDetails?.caseNumber}
                                        </Typography>
                                        <Typography tag="p" size="XS">
                                            {L(
                                                'LØN_DETALJE_MODTAGET_DATO_LABEL'
                                            )}
                                            {': '}
                                            {formatDate(
                                                new Date(
                                                    caseDetails?.receivedDate
                                                )
                                            )}
                                        </Typography>
                                        <Typography
                                            tag="p"
                                            size="XS"
                                            style={{
                                                color:
                                                    caseDetails?.caseStatus
                                                        ?.statusType ===
                                                    'FristOverskredet'
                                                        ? colors.red500
                                                        : colors.black,
                                            }}
                                        >
                                            {caseDetails?.isClosed
                                                ? L(
                                                      'LØN_DETALJE_AFSLUTTET_DATO_LABEL'
                                                  )
                                                : L(
                                                      'LØN_DETALJE_FRIST_DATO_LABEL'
                                                  )}
                                            {': '}
                                            {caseDetails?.isClosed
                                                ? formatDate(
                                                      new Date(
                                                          caseDetails?.caseProcessingEndDate
                                                      )
                                                  )
                                                : formatDate(
                                                      new Date(
                                                          caseDetails?.deadlineDate
                                                      )
                                                  )}
                                        </Typography>
                                        <Typography tag="p" size="XS">
                                            {L('LØN_DETALJE_STATUS_LABEL')}
                                            {': '}
                                            {
                                                caseDetails?.caseStatus
                                                    ?.statusNavn
                                            }
                                        </Typography>

                                        {!caseDetails?.isClosed ? (
                                            <div
                                                style={{
                                                    flexGrow: 1,
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                {caseDetails?.allowedUpdateActionList.includes(
                                                    'Accept'
                                                ) && (
                                                    <TakeCase
                                                        currentCase={Case.buildCaseFromCaseDetails(
                                                            caseDetails
                                                        )}
                                                        onSuccess={() => {
                                                            init(
                                                                caseDetails.caseId
                                                            )
                                                        }}
                                                        useRegularButton
                                                    />
                                                )}
                                                <div
                                                    style={{
                                                        height: '100%',
                                                        marginLeft: '0.2rem',
                                                        display: 'flex',
                                                    }}
                                                >
                                                    {caseDetails &&
                                                        !caseDetails?.isClosed &&
                                                        !caseDetails?.allowedUpdateActionList.includes(
                                                            'Accept'
                                                        ) && (
                                                            <Reminders
                                                                currentCase={
                                                                    caseDetails
                                                                }
                                                                onUpdated={() => {
                                                                    init(
                                                                        caseDetails.caseId
                                                                    )
                                                                }}
                                                            />
                                                        )}
                                                    <Dropdown
                                                        trigger={
                                                            <Button
                                                                type="ghost"
                                                                style={{
                                                                    padding:
                                                                        '0.2rem 0.4rem',
                                                                    borderRadius:
                                                                        '0.3rem',
                                                                    border: '1px solid',
                                                                    borderColor:
                                                                        colors.green500,
                                                                    backgroundColor:
                                                                        'white',
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        position:
                                                                            'relative',
                                                                        top: '3px',
                                                                    }}
                                                                >
                                                                    <ThreeDots
                                                                        width="22px"
                                                                        fill={
                                                                            colors.green500
                                                                        }
                                                                    />
                                                                </div>
                                                            </Button>
                                                        }
                                                        items={[
                                                            ...(caseDetails?.allowedUpdateActionList.includes(
                                                                'SammenfletMedAndenSag'
                                                            )
                                                                ? [
                                                                      {
                                                                          id: 'Attach',
                                                                          label: L(
                                                                              'LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_LABEL'
                                                                          ),
                                                                          onClick:
                                                                              () => {
                                                                                  setAttachCaseOpen(
                                                                                      true
                                                                                  )
                                                                              },
                                                                          icon: (
                                                                              <IkonSammenlaeg />
                                                                          ),
                                                                          disabled:
                                                                              authProfile.isImitator,
                                                                      },
                                                                  ]
                                                                : []),
                                                            ...(caseDetails?.allowedUpdateActionList.includes(
                                                                'Afvis'
                                                            )
                                                                ? [
                                                                      {
                                                                          id: 'delete',
                                                                          label: L(
                                                                              'LØN_OVERSIGT_AFVIS_SAG_LABEL'
                                                                          ),
                                                                          color: colors.red600,
                                                                          icon: (
                                                                              <IkonAfvissag
                                                                                  fill={
                                                                                      colors.red600
                                                                                  }
                                                                              />
                                                                          ),
                                                                          onClick:
                                                                              () =>
                                                                                  setRejectCaseOpen(
                                                                                      true
                                                                                  ),
                                                                          disabled:
                                                                              authProfile.isImitator,
                                                                      },
                                                                  ]
                                                                : []),
                                                        ]}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ width: '20%' }} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {windowSize.width < breakpoints.Desktop && (
                        <>
                            <BackLink
                                to={`/mine-lonindplaceringer${
                                    localStorage.getItem('salary-last-tab') ===
                                    'closed'
                                        ? '?tab=lukket'
                                        : ''
                                }`}
                                label={L(
                                    'HEADER_FANEBLAD_LONINDPLACERINGER-TITEL'
                                )}
                            />

                            {caseDetails?.allowedUpdateActionList.includes(
                                'Accept'
                            ) && (
                                <div className={classes.takeCaseContainer}>
                                    <TakeCase
                                        currentCase={Case.buildCaseFromCaseDetails(
                                            caseDetails
                                        )}
                                        onSuccess={() => {
                                            init(caseDetails.caseId)
                                        }}
                                        useRegularButton
                                    />
                                </div>
                            )}

                            <div className={classes.headerMobile}>
                                <div
                                    style={{
                                        position: 'absolute',
                                        right: '0',
                                        top: '0',
                                    }}
                                >
                                    <Dropdown
                                        trigger={
                                            <Button
                                                type="ghost"
                                                style={{
                                                    padding: '0.2rem 0.4rem',
                                                    backgroundColor: 'white',
                                                    rotate: '90deg',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        position: 'relative',
                                                        top: '3px',
                                                    }}
                                                >
                                                    <ThreeDots
                                                        width="22px"
                                                        fill={colors.green500}
                                                    />
                                                </div>
                                            </Button>
                                        }
                                        items={[
                                            ...(caseDetails?.allowedUpdateActionList.includes(
                                                'SammenfletMedAndenSag'
                                            )
                                                ? [
                                                      {
                                                          id: 'Attach',
                                                          label: L(
                                                              'LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_LABEL'
                                                          ),
                                                          onClick: () => {
                                                              setAttachCaseOpen(
                                                                  true
                                                              )
                                                          },
                                                          icon: (
                                                              <IkonSammenlaeg />
                                                          ),
                                                          disabled:
                                                              authProfile.isImitator,
                                                      },
                                                  ]
                                                : []),
                                            ...(caseDetails?.allowedUpdateActionList.includes(
                                                'Afvis'
                                            )
                                                ? [
                                                      {
                                                          id: 'delete',
                                                          label: L(
                                                              'LØN_OVERSIGT_AFVIS_SAG_LABEL'
                                                          ),
                                                          color: colors.red600,
                                                          icon: (
                                                              <IkonAfvissag
                                                                  fill={
                                                                      colors.red600
                                                                  }
                                                              />
                                                          ),
                                                          onClick: () =>
                                                              setRejectCaseOpen(
                                                                  true
                                                              ),
                                                          disabled:
                                                              authProfile.isImitator,
                                                      },
                                                  ]
                                                : []),
                                        ]}
                                    />
                                </div>
                                <Typography tag="p" size="XXS">
                                    {L('LØN_DETALJE_SAGSDETALJER_LABEL_MOBIL')}:
                                </Typography>
                                <Typography
                                    mobileSize="XL"
                                    tag="h1"
                                    size="XXXXL"
                                    spacing=".2rem"
                                >
                                    {caseDetails?.newlyHired.name}
                                </Typography>
                                <div className={classes.headerMobileDetails}>
                                    <Typography tag="p" size="XXS">
                                        {L('LØN_DETALJE_CASE_ID_LABEL')}
                                        {': '}
                                        {caseDetails?.caseNumber}
                                    </Typography>
                                    <Typography tag="p" size="XXS">
                                        {L('LØN_DETALJE_MODTAGET_DATO_LABEL')}
                                        {': '}
                                        {formatDate(
                                            new Date(caseDetails?.receivedDate)
                                        )}
                                    </Typography>
                                    <Typography
                                        tag="p"
                                        size="XXS"
                                        style={{
                                            color:
                                                caseDetails?.caseStatus
                                                    ?.statusType ===
                                                'FristOverskredet'
                                                    ? colors.red500
                                                    : colors.black,
                                        }}
                                    >
                                        {caseDetails?.isClosed
                                            ? L(
                                                  'LØN_DETALJE_AFSLUTTET_DATO_LABEL'
                                              )
                                            : L('LØN_DETALJE_FRIST_DATO_LABEL')}
                                        {': '}
                                        {caseDetails?.isClosed
                                            ? formatDate(
                                                  new Date(
                                                      caseDetails?.caseProcessingEndDate
                                                  )
                                              )
                                            : formatDate(
                                                  new Date(
                                                      caseDetails?.deadlineDate
                                                  )
                                              )}
                                    </Typography>
                                    <Typography tag="p" size="XXS">
                                        {L('LØN_DETALJE_STATUS_LABEL')}
                                        {': '}
                                        {caseDetails?.caseStatus?.statusNavn}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    )}
                    <div className={classes.section}>
                        <hr />
                        {isLoading ? (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <Spinner />
                            </div>
                        ) : (
                            <CardColumnContainer>
                                <div>
                                    <PersonalDetails
                                        caseDetails={caseDetails}
                                        updateData={(data) =>
                                            setCaseDetails(data)
                                        }
                                    />
                                </div>
                                <div>
                                    <CaseFilesRow
                                        caseDetails={caseDetails}
                                        onUpdated={() => {
                                            init(caseDetails.caseId)
                                        }}
                                    />
                                    <CaseNotesRow
                                        caseDetails={caseDetails}
                                        onUpdated={() => {
                                            init(caseDetails.caseId)
                                        }}
                                    />
                                    {caseDetails?.mergedCaseList?.length &&
                                    !caseDetails?.isClosed ? (
                                        <MergedCasesRow
                                            caseDetails={caseDetails}
                                            onUpdated={() => {
                                                init(caseDetails.caseId)
                                            }}
                                        />
                                    ) : null}
                                </div>
                                <div>
                                    {!caseDetails?.isClosed && (
                                        <CaseHelpColumn />
                                    )}
                                    {caseDetails?.mergedCaseList?.length &&
                                    caseDetails?.isClosed ? (
                                        <MergedCasesRow
                                            caseDetails={caseDetails}
                                            onUpdated={() => {
                                                init(caseDetails.caseId)
                                            }}
                                        />
                                    ) : null}
                                    {caseDetails && !caseDetails?.isClosed && (
                                        <div
                                            style={{
                                                marginBottom: '1rem',
                                                width: '100%',
                                            }}
                                        >
                                            <div style={{ width: '100%' }}>
                                                <ContactDialog
                                                    currentCase={caseDetails}
                                                    onUpdated={() => {
                                                        init(caseDetails.caseId)
                                                    }}
                                                    onError={(error) => {
                                                        console.error(error)
                                                    }}
                                                />
                                            </div>
                                            <div />
                                        </div>
                                    )}

                                    <CaseReportingRow
                                        caseDetails={caseDetails}
                                        onUpdated={() => {
                                            init(caseDetails.caseId)
                                        }}
                                    />
                                    {caseDetails && !caseDetails?.isClosed && (
                                        <CloseCaseRow
                                            caseDetails={caseDetails}
                                            onUpdated={() => {
                                                init(caseDetails.caseId)
                                            }}
                                        />
                                    )}
                                </div>
                            </CardColumnContainer>
                        )}
                    </div>

                    {/* Dialog components - rendered regardless of screen size */}
                    {attachCaseOpen && (
                        <AttachToAnotherCase
                            currentCase={Case.buildCaseFromCaseDetails(
                                caseDetails
                            )}
                            onSuccess={() => {
                                setAttachCaseOpen(false)
                                init(caseDetails?.caseId)
                            }}
                            openCases={openCases}
                            onCancel={() => {
                                setAttachCaseOpen(false)
                            }}
                            redirectToParentCase
                        />
                    )}
                    {rejectCaseOpen && (
                        <RejectCase
                            caseId={caseDetails.caseId}
                            caseRegistrationDate={
                                caseDetails.caseRegistrationTime
                            }
                            open={!!rejectCaseOpen}
                            handleClose={() => setRejectCaseOpen(false)}
                            onSuccess={() => {
                                setRejectCaseOpen(false)
                                init(caseDetails.caseId)
                            }}
                            navigateToCaseList
                        />
                    )}
                </div>
            )}
        </>
    )
}

export { CaseDetails }
